import React, { useEffect, useState } from "react";
import FeatherIcon from "feather-icons-react";
import {
  disablePushSubscriptions,
  getNotificationPreferences,
  saveNotificationPreferences,
} from "../../helpers/notification_helper";
import { registerDesktopPushSubscription } from "../../helpers/desktop_notification_helper";
import "./Notifications.scss";

const types = [
  ["TaskAssigned", "Task assigned"],
  ["ReviewAssigned", "Review assigned"],
  ["DataPreparationAssigned", "Data preparation assigned"],
  ["QueryAssigned", "Query requires response"],
  ["QueryComment", "Query comments and follow-ups"],
  ["QueryResponseReview", "Query response review and approval"],
  ["QueryResponseReceived", "Query responses received"],
  ["ReviewDiscussionComment", "Review discussion comments"],
  ["FindingAssigned", "Finding assigned"],
  ["FindingResponseAssigned", "Finding response required"],
  ["ApprovalRequired", "Approval required"],
  ["ImportCompleted", "Import completed"],
  ["ReviewFeedbackReceived", "Review feedback"],
  ["TaskDueSoon", "Due-date reminders"],
];

const NotificationSettingsPage = () => {
  const [preferences, setPreferences] = useState({});
  const [permission, setPermission] = useState(
    typeof Notification === "undefined"
      ? "unsupported"
      : Notification.permission
  );
  const [saving, setSaving] = useState(false);
  const [desktopEnabled, setDesktopEnabled] = useState(false);
  const [desktopMessage, setDesktopMessage] = useState("");
  useEffect(() => {
    getNotificationPreferences()
      .then((response) => {
        if (response?.status)
          setPreferences(
            Object.fromEntries(
              (response.data || []).map((item) => [item.notificationType, item])
            )
          );
      })
      .catch(() => undefined);

    const getSubscription = async () => {
      if (
        !("serviceWorker" in navigator) ||
        Notification.permission !== "granted" ||
        localStorage.getItem("piers.desktopNotificationsDisabled") === "true"
      )
        return;
      const registration = await navigator.serviceWorker.getRegistration(
        "/notification-sw.js"
      );
      const subscription = await registration?.pushManager.getSubscription();
      setDesktopEnabled(Boolean(subscription));
    };
    getSubscription().catch(() => setDesktopEnabled(false));
  }, []);

  const enabled = (type) => preferences[type]?.desktopEnabled !== false;
  const supportsEmail = (type) => ["QueryAssigned", "QueryComment", "QueryResponseReview", "QueryResponseReceived", "ReviewDiscussionComment"].includes(type);
  const emailEnabled = (type) => supportsEmail(type) && preferences[type]?.emailEnabled !== false;

  const toggle = (type) =>
    setPreferences((all) => ({
      ...all,
      [type]: {
        ...(all[type] || { notificationType: type }),
        notificationType: type,
        desktopEnabled: !enabled(type),
      },
    }));

  const save = async () => {
    setSaving(true);
    try {
      await saveNotificationPreferences(
        types.map(([type]) => ({
          notificationType: type,
          desktopEnabled: enabled(type),
          emailEnabled: supportsEmail(type) ? emailEnabled(type) : (preferences[type]?.emailEnabled ?? false),
        }))
      );
    } finally {
      setSaving(false);
    }
  };

  const enableDesktop = async () => {
    setDesktopMessage("");
    if (!("Notification" in window)) {
      setPermission("unsupported");
      setDesktopMessage("This browser does not support desktop notifications.");
      return;
    }
    if (!("serviceWorker" in navigator) || !window.isSecureContext) {
      setDesktopMessage(
        "Desktop notifications require HTTPS or a localhost connection."
      );
      return;
    }
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== "granted") return;
      localStorage.removeItem("piers.desktopNotificationsDisabled");
      const registrationResult = await registerDesktopPushSubscription();
      if (!registrationResult.registered) {
        setDesktopMessage(registrationResult.reason);
        return;
      }
      localStorage.removeItem("piers.desktopNotificationsDisabled");
      setDesktopEnabled(true);
      setDesktopMessage("Desktop notifications are enabled for this browser.");
    } catch {
      setDesktopEnabled(false);
      setDesktopMessage(
        "Desktop notifications could not be enabled. Please try again."
      );
    }
  };

  const disableDesktop = async () => {
    const registration = await navigator.serviceWorker.getRegistration(
      "/notification-sw.js"
    );
    const subscription = await registration?.pushManager.getSubscription();
    await subscription?.unsubscribe();
    await disablePushSubscriptions();
    localStorage.setItem("piers.desktopNotificationsDisabled", "true");
    setDesktopEnabled(false);
    setDesktopMessage("");
  };
  const desktopLabel =
    permission === "granted" && desktopEnabled
      ? "Enabled"
      : permission === "denied"
      ? "Blocked by browser"
      : permission === "unsupported"
      ? "Not supported"
      : "Not enabled";
  return (
    <div className="page-content notification-page piers-page">
      <div className="container-fluid">
        <div className="notification-page__header piers-page-header">
          <div>
            <h2 className="piers-page-title">Notification Preferences</h2>
            <p>Choose which PIERS activity can alert you on this device.</p>
          </div>
          <button className="btn btn-primary" disabled={saving} onClick={save}>
            {saving ? "Saving…" : "Save preferences"}
          </button>
        </div>
        <section className="notification-day">
          <div className="d-flex justify-content-between align-items-center p-3">
            <div>
              <h5 className="p-0 bg-transparent mb-1">Desktop Notifications</h5>
              <span className="text-muted">
                {permission === "denied"
                  ? "Desktop notifications are blocked in your browser settings."
                  : "Receive alerts for important PIERS activity while working in another tab."}
              </span>
              {desktopMessage && (
                <div
                  className={`small mt-2 ${
                    desktopEnabled ? "text-success" : "text-danger"
                  }`}
                >
                  {desktopMessage}
                </div>
              )}
            </div>
            <div>
              <span
                className={`badge ${
                  permission === "granted" && desktopEnabled
                    ? "bg-success"
                    : "bg-secondary"
                } me-2`}
              >
                {desktopLabel}
              </span>
              {permission !== "denied" &&
                (!desktopEnabled || permission === "default") && (
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={enableDesktop}
                  >
                    Enable
                  </button>
                )}
              {permission === "granted" && desktopEnabled && (
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={disableDesktop}
                >
                  Disable
                </button>
              )}
            </div>
          </div>
        </section>
        <section className="notification-day">
          <h5>Delivery preferences</h5>
          <div className="table-responsive">
            <table className="table mb-0 align-middle">
              <thead>
                <tr>
                  <th>Notification</th>
                  <th>In app</th>
                  <th>Desktop</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {types.map(([type, label]) => (
                  <tr key={type}>
                    <td>{label}</td>
                    <td>
                      <span className="text-success">Always</span>
                    </td>
                    <td>
                      <div className="form-check form-switch mb-0">
                        <input
                          aria-label={`${label} desktop notifications`}
                          className="form-check-input"
                          type="checkbox"
                          checked={enabled(type)}
                          onChange={() => toggle(type)}
                          disabled={permission === "denied"}
                        />
                      </div>
                    </td>
                    <td>
                      {supportsEmail(type) ? (
                        <input className="form-check-input" type="checkbox"
                          aria-label={`${label} email notifications`} checked={emailEnabled(type)}
                          onChange={() => setPreferences((all) => ({ ...all, [type]: {
                            ...(all[type] || { notificationType: type }), emailEnabled: !emailEnabled(type),
                          } }))} />
                      ) : <span className="text-muted">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};
export default NotificationSettingsPage;
